/** @format */

import { axiosInstance } from '@common/application/context/Providers';
import { AxiosApiResponse } from '@common/types/api';
import { ElasticQueryOpts, ElasticResponse, ElasticSort } from '@common/types/elastic';
import { Nullable } from '@common/types/utils';

export class Contact {
  id: number;
  siteId: number;
  name: string;
  role: string;
  phone: string;
  email: string;
  createdAt: string;
  approvedAt: string | null | Date;
  proposedBy?: string;
  updatedAt: string;
  deletedAt: string;

  constructor(config: { siteId: number; name: string; role: string; phone: string; email: string; approvedAt: Date | null | string; proposedBy?: string }) {
    this.siteId = config.siteId;
    this.name = config.name;
    this.role = config.role;
    this.phone = config.phone;
    this.email = config.email;
    this.approvedAt = config.approvedAt;
    this.proposedBy = config.proposedBy;
  }

  static list(
    query,
    sort,
    queryOpts: ElasticQueryOpts,
    size?: number,
    pit?: Nullable<string>,
    search_after?: Nullable<ElasticSort>,
    signal?: AbortSignal,
  ): ElasticResponse<Contact> {
    // Ensure this code is not ran by SSR!
    if (typeof window !== 'undefined') {
      return axiosInstance
        .get(`/api/v1/contacts`, {
          signal,
          params: {
            sequence_id: 1,
            pit,
            query,
            queryOpts: JSON.stringify(queryOpts ?? { deleted: false }),
            sort: JSON.stringify(sort),
            search_after: JSON.stringify(search_after),
            size,
          },
        })
        .then((res) => ({ ...res.data }));
    }
    throw new Error();
  }

  static get(id: number): AxiosApiResponse<Contact> {
    return axiosInstance.get(`/api/v1/contacts/${id}`).then((res) => ({ ...res.data }));
  }

  static delete(id: number): Promise<Record<string, unknown>> {
    return axiosInstance.delete(`/api/v1/contacts/${id}`).then((res) => ({ ...res.data }));
  }

  static undelete(id: number): Promise<Record<string, unknown>> {
    return axiosInstance.post(`/api/v1/contacts/${id}/undelete`).then((res) => ({ ...res.data }));
  }

  static approve(id: number): Promise<Record<string, unknown>> {
    return axiosInstance.post(`/api/v1/contacts/${id}/approve`).then((res) => ({ ...res.data }));
  }

  static update(
    updatedContact: {
      siteId: number;
      name: string;
      role: string;
      phone: string;
      email: string;
    },
    id: number,
  ): Promise<Record<string, unknown>> {
    return axiosInstance.patch(`/api/v1/contacts/${id}`, { payload: updatedContact }).then((res) => ({ ...res.data }));
  }

  static getAddTemplate(locale: string): Promise<Record<string, unknown>> {
    return axiosInstance.get(`/api/v1/templates/contacts/add`, {
      // responseType: 'arraybuffer',
      params: {
        locale: locale,
      },
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  static postAddTemplate(file: File): Promise<Record<string, unknown>> {
    return axiosInstance.post(
      `/api/v1/templates/contacts/add`,
      { payload: file },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );
  }

  add(): Promise<Record<string, unknown>> {
    return axiosInstance
      .post(`/api/v1/contacts`, {
        payload: this,
      })
      .then((res) => ({ ...res.data }));
  }

  suggest(): Promise<Record<string, unknown>> {
    return axiosInstance
      .post(`/api/v1/contacts/suggest`, {
        payload: this,
      })
      .then((res) => ({ ...res.data }));
  }
}
